import { router, usePage, usePoll } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-351 — drive one report quality-check run for the findings panel.
 *
 * The truth lives in the `reportQc` Inertia prop, exactly as the upload half
 * does (`useReportUpload`). Nothing about a run is kept in component state and
 * nothing is kept in `localStorage`: a finding's resolution goes to the server
 * and is read back from the prop, because a screenshot cannot tell a saved
 * value from a value that merely rendered (design-spec §5, the RED-235 lesson).
 *
 * Three things here are not decoration and must not be simplified away:
 *
 * 1. **The mount-time resume.** A run takes 45-180 seconds. If the page is
 *    reloaded or navigated away from mid-run, the poll must pick the run back
 *    up instead of stranding the appraiser on a dead screen. Same shape as
 *    `useExportDetail.js:471-481`, which does this for `activeExport`.
 *
 * 2. **Nothing paid starts without an explicit click** (prototype v3, which
 *    replaced design-spec §17.3's three-second countdown). A successful read
 *    hands over to `enterFileReady(live)`, which opens the cost dialog
 *    `Kvaliteedikontroll on tasuline`; every other way to start —
 *    `Käivita kontroll kohe`, `Käivita kontroll uuesti`, `Proovi uuesti` — goes
 *    through `requestStart()` and the same dialog. Only its `Käivita kontroll`
 *    calls `startNow()`. Arriving at an already-uploaded file on a COLD page
 *    load is not live, so the dialog stays shut and nothing starts: a page
 *    refresh can never buy a second paid run, and `Katkesta` needs no stored
 *    flag to stay cancelled.
 *
 * 3. **The screen can never be left stale** (RED-351 rework P1, 2026-09-24).
 *    Siim watched evaluation 4816 sit at "Kontroll käib. 85 s." after the job
 *    had already failed; only a manual reload showed the result. The two-second
 *    poll alone cannot promise a fresh screen: browsers throttle, freeze or
 *    discard a page that is out of sight (Chrome's frozen tabs, Edge's sleeping
 *    tabs), and on Herd every request shares four PHP workers with the
 *    evaluation page's 30-second calls, so one answer can be very late. So:
 *    - the run is read again the moment the appraiser comes back (`focus` for
 *      a window that was only behind PowerShell, `visibilitychange` for a
 *      hidden tab or a minimised window) and the poll chain is restarted then;
 *    - a watchdog restarts a poll chain that has had no answer for 12 s —
 *      `mode: 'rest'` only schedules the next poll when the previous one
 *      finishes, so one request that never answers would hold it forever;
 *    - the poll stops when the run is no longer queued/processing. It used to
 *      run on every two seconds for as long as the tab stayed open;
 *    - the waiting clock is counted on the server (`elapsedSeconds`), so a
 *      reload resumes at 85 s instead of restarting at zero, and the give-up
 *      moment (`giveUpAfterSeconds`, sent by the server) is not reset by a
 *      reload either.
 *    Playwright cannot show the original bug: its Chromium forces every page
 *    visible and turns background throttling off, which is why the 2026-09-17
 *    headless run updated within 3 s. Spec 36 pauses the page clock instead —
 *    a frozen tab — and proves that coming back is enough.
 *
 * @param {string|number} evaluationId
 */
export function useReportCheck(evaluationId) {
  const page = usePage()
  const toast = useToast()
  const { t } = useI18n()

  /**
   * RED-373: the PATCH goes through its OWN axios instance, without the global
   * 419/401 interceptor of `bootstrap.js`. That interceptor subscribes the
   * retry only AFTER its token refresh has already flushed the subscribers, so
   * a 419 there never settles: the card stayed busy for good and the appraiser
   * saw nothing (browser pass, 2026-10-01). Here a 419 reaches the catch below
   * and says "reload the page". Same credentials and XSRF cookie handling.
   */
  const resolveClient = axios.create({
    withCredentials: true,
    withXSRFToken: true,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })

  /**
   * Fallback give-up for a payload from before the server sent its own
   * `giveUpAfterSeconds` (queued: the stale limit; processing: the job timeout
   * plus a margin, counted from pickup — always above the engine's deadline).
   */
  const FALLBACK_GIVE_UP_SECONDS = 15 * 60
  const POLL_INTERVAL_MS = 2000
  /** No poll has answered for this long while a run is going: restart the chain. */
  const STALL_AFTER_MS = 12_000
  /** `focus` and `visibilitychange` usually arrive together; one re-read is enough. */
  const RESYNC_GAP_MS = 1_000

  const check = computed(() => page.props.reportQc?.check ?? null)
  const upload = computed(() => page.props.reportQc?.upload ?? null)
  const supportEmail = computed(() => page.props.reportQc?.supportEmail ?? '')
  const findings = computed(() => check.value?.findings ?? [])
  const checklist = computed(() => check.value?.checklist ?? null)

  const starting = shallowRef(false)
  const timedOut = shallowRef(false)
  /** The status the run had when the page gave up: 'queued' or 'processing'. */
  const timedOutWhile = shallowRef(null)
  const startError = ref(null)
  /** The cost dialog `Kvaliteedikontroll on tasuline` is open. */
  const confirmOpen = shallowRef(false)
  const elapsedSeconds = shallowRef(0)

  let clockTimer = null
  let tracking = false
  let lastPollAnsweredAt = 0
  let lastResyncAt = 0
  /** The server's elapsed seconds at the moment the latest read arrived. */
  let elapsedAnchor = { seconds: 0, at: 0 }

  const isRunning = computed(
    () => check.value !== null && ['queued', 'processing'].includes(check.value.status),
  )

  /**
   * The page gave up on a run that never left the queue. The server retires
   * such a row on the next start, so starting again is allowed and really
   * queues a new run. A run that is PROCESSING is never restarted from here:
   * it is still working on the server, and a start would only return it.
   */
  const canRestartStale = computed(
    () => timedOut.value && timedOutWhile.value === 'queued' && check.value?.status === 'queued',
  )

  const { start: startPoll, stop: stopPoll } = usePoll(
    POLL_INTERVAL_MS,
    {
      only: ['reportQc'],
      async: true,
      onFinish: () => {
        lastPollAnsweredAt = Date.now()
      },
    },
    { autoStart: false, keepAlive: true, mode: 'rest' },
  )

  const clearClock = () => {
    if (clockTimer !== null) {
      clearInterval(clockTimer)
      clockTimer = null
    }
  }

  // Re-anchor the waiting clock on every fresh read of a running run.
  watch(
    () => check.value?.elapsedSeconds,
    seconds => {
      if (seconds === null || seconds === undefined) {
        return
      }
      elapsedAnchor = { seconds, at: Date.now() }
      elapsedSeconds.value = seconds
    },
    { immediate: true },
  )

  const stopTracking = () => {
    tracking = false
    clearClock()
    stopPoll()
  }

  const tick = () => {
    if (elapsedAnchor.at > 0) {
      elapsedSeconds.value = elapsedAnchor.seconds + Math.floor((Date.now() - elapsedAnchor.at) / 1000)
    }
    const giveUpAfter = check.value?.giveUpAfterSeconds ?? FALLBACK_GIVE_UP_SECONDS
    if (elapsedSeconds.value > giveUpAfter) {
      timedOutWhile.value = check.value?.status ?? null
      timedOut.value = true
      stopTracking()
      return
    }
    // A poll chain that has had no answer for a while is restarted. Only while
    // the page is in sight: a hidden tab is re-read when it comes back anyway.
    if (document.visibilityState === 'visible' && Date.now() - lastPollAnsweredAt > STALL_AFTER_MS) {
      lastPollAnsweredAt = Date.now()
      startPoll()
    }
  }

  /**
   * Follow a run until it is no longer queued/processing. Safe to call twice:
   * a second call only restarts the poll chain.
   */
  const track = () => {
    timedOut.value = false
    lastPollAnsweredAt = Date.now()
    if (!tracking) {
      tracking = true
      clearClock()
      clockTimer = setInterval(tick, 1000)
    }
    startPoll()
  }

  const refresh = () =>
    new Promise(resolve => {
      router.reload({ only: ['reportQc'], onFinish: resolve })
    })

  // The run ended: stop asking. A run the page did not start (a second tab, a
  // re-read on return) is picked up the same way.
  watch(isRunning, running => {
    if (running && !tracking && !timedOut.value) {
      track()
    } else if (!running) {
      timedOut.value = false
      if (tracking) {
        stopTracking()
      }
    }
  })

  /**
   * Ask the server to start a run. 202 means it is queued; 422 with
   * `code: no_file` means nothing has been read yet.
   */
  const startNow = async () => {
    if (starting.value || (isRunning.value && !canRestartStale.value)) {
      return
    }
    starting.value = true
    startError.value = null
    try {
      await axios.post(route('evaluation.report-check.store', evaluationId))
      await refresh()
      track()
    } catch (err) {
      console.error('Report check start failed', {
        status: err?.response?.status,
        body: err?.response?.data,
      })
      startError.value = err?.response?.data?.code ?? 'generic'
    } finally {
      starting.value = false
    }
  }

  /**
   * The appraiser asked for a run: open the cost dialog. Nothing starts until
   * they press `Käivita kontroll` in it.
   */
  const requestStart = () => {
    if (starting.value || (isRunning.value && !canRestartStale.value)) {
      return
    }
    startError.value = null
    confirmOpen.value = true
  }

  /**
   * A file is on screen. `live` is the whole point — see the header comment:
   * only a read that just happened opens the dialog by itself. No
   * `check === null` guard on purpose: a REPLACED file must ask again even
   * though the previous run's row is still the latest one.
   */
  const enterFileReady = (live = true) => {
    if (live) {
      requestStart()
    }
  }

  /** `Käivita kontroll` in the cost dialog: the one place a paid run starts. */
  const confirmStart = () => {
    confirmOpen.value = false
    startNow()
  }

  /** `Katkesta`, Esc or a click outside the dialog: nothing starts. */
  const cancelStart = () => {
    confirmOpen.value = false
  }

  /** A new file was read, or the old one removed — forget the last attempt. */
  const resetForNewFile = () => {
    confirmOpen.value = false
    startError.value = null
    timedOut.value = false
  }

  /**
   * Mark a finding fixed, or clear the mark by passing null.
   *
   * The card sends only `'fixed'` or `null` since the RED-388 follow-up
   * (2026-10-15). The server still accepts `'not_an_error'`, so an old row
   * stored with it keeps its badge and can be cleared with null. The prop is
   * re-read afterwards rather than the JSON echo being trusted, so what the
   * screen shows is what a refresh would also show.
   *
   * @param {number|string} findingId
   * @param {'fixed'|null} resolution
   */
  const resolveFinding = async (findingId, resolution) => {
    try {
      await resolveClient.patch(
        route('evaluation.report-check.resolve', [evaluationId, findingId]),
        { resolution },
      )
      await refresh()
      return true
    } catch (err) {
      const status = err?.response?.status
      console.error('Report check resolve failed', {
        status,
        body: err?.response?.data,
      })
      // RED-373: a failed save must be SEEN — the card stays as it was, and
      // without this the click looks like it did nothing. There is no
      // flash→toast bridge, so the toast is raised here. An expired session
      // (419 CSRF, 401 logged out — a long read does that) gets the
      // session sentence: retrying will not help until the page is reloaded.
      const sessionExpired = status === 419 || status === 401
      toast.add({
        severity: 'error',
        summary: t('messages.evaluation.qualityCheck.toast.resolveFailedSummary'),
        detail: sessionExpired
          ? t('messages.detail.reportQc.errors.csrfExpired')
          : t('messages.evaluation.qualityCheck.toast.resolveFailedDetail'),
        life: sessionExpired ? 0 : 8000,
      })
      return false
    }
  }

  /**
   * RED-388: `Kas see leid oli õige?` — save the owner's verdict, and the note
   * when one is passed (`undefined` = keep the stored note, a verdict click).
   * Resolves to `{ ok: true }`, or `{ ok: false, noteError }` when the server
   * refused the note (a personal code, too long): that error is shown under
   * the field, not as a toast, because retrying will not help until the text
   * changes. Any other failure raises the toast, as `resolveFinding` does.
   */
  const sendFeedback = async (findingId, verdict, note = undefined) => {
    const body = note === undefined ? { verdict } : { verdict, feedback_note: note }
    try {
      await resolveClient.put(route('evaluation.report-check.feedback', [evaluationId, findingId]), body)
      await refresh()
      return { ok: true }
    } catch (err) {
      const noteError = err?.response?.status === 422 ? err.response.data?.errors?.feedback_note?.[0] : null
      if (noteError) {
        return { ok: false, noteError }
      }
      feedbackFailed(err)
      return { ok: false, noteError: null }
    }
  }

  /** RED-388: clicking the chosen verdict again takes the answer back. */
  const clearFeedback = async findingId => {
    try {
      await resolveClient.delete(route('evaluation.report-check.feedback.clear', [evaluationId, findingId]))
      await refresh()
      return { ok: true }
    } catch (err) {
      feedbackFailed(err)
      return { ok: false, noteError: null }
    }
  }

  /** Never logs the request body: the note is the appraiser's free text. */
  const feedbackFailed = err => {
    const status = err?.response?.status
    console.error('Report check feedback failed', { status })
    const sessionExpired = status === 419 || status === 401
    toast.add({
      severity: 'error',
      summary: t('messages.evaluation.qualityCheck.feedback.saveFailedSummary'),
      detail: sessionExpired
        ? t('messages.detail.reportQc.errors.csrfExpired')
        : t('messages.evaluation.qualityCheck.feedback.saveFailedDetail'),
      life: sessionExpired ? 0 : 8000,
    })
  }

  /**
   * The appraiser is back: read the run again NOW, and give a running run a
   * fresh poll chain — a request stuck in flight can no longer hold it.
   */
  const resync = () => {
    const now = Date.now()
    if (now - lastResyncAt < RESYNC_GAP_MS) {
      return
    }
    lastResyncAt = now
    refresh()
    if (tracking) {
      lastPollAnsweredAt = now
      startPoll()
    }
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      resync()
    }
  }

  // A run queued before a refresh or a navigation is still in flight — pick it
  // back up so the result lands instead of the appraiser watching a dead page.
  if (isRunning.value) {
    track()
  }

  onMounted(() => {
    window.addEventListener('focus', resync)
    document.addEventListener('visibilitychange', onVisibilityChange)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('focus', resync)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    stopTracking()
  })

  return {
    check,
    upload,
    supportEmail,
    findings,
    checklist,
    isRunning,
    starting,
    timedOut,
    timedOutWhile,
    canRestartStale,
    startError,
    confirmOpen,
    elapsedSeconds,
    enterFileReady,
    requestStart,
    confirmStart,
    cancelStart,
    resetForNewFile,
    resolveFinding,
    sendFeedback,
    clearFeedback,
    refresh,
  }
}
